import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { createMap, getMap, getMapJob, getMapStatus, prefilter } from "@/api/endpoints";
import { suggestedReason } from "@/lib/remap";
import { FIELD_STYLES } from "@/lib/fieldStyles";
import { ApiError } from "@/api/client";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextField } from "@/components/ui/TextField";
import { useSessionState } from "@/lib/useSessionState";
import type { Assessment, MapJob, PrefilterResponse } from "@/api/types";
import { SuggestionsView } from "@/components/SuggestionsView";
import { CapabilityLink, CapabilityLinks } from "@/components/RegistryLinks";
import { ago } from "@/lib/time";

/** How often to ask the backend whether a running map job has finished. */
const POLL_MS = 3000;
// Everything on this page persists per tab (see useSessionState), so a carefully
// written description — and its prefilter, result, or in-flight map — survives
// clicking away and coming back.
const KEY = "capability-registry:map:";

/** The backend's error messages joined for display, or the error itself if it wasn't an ApiError. */
function errorText(e: unknown): string {
  return e instanceof ApiError ? e.errors.join("; ") : String(e);
}

/** Why the input was turned away, and by which check — either can be overridden. */
interface Rejection {
  by: "rules" | "model";
  reasons: string[];
}

/** A map job still running server-side. Persisted, so leaving the page mid-run and
 * coming back resumes watching it rather than losing track of it. */
interface ActiveJob {
  id: string;
  startedAt: number;
}

/** Turns a product description into a capability map.
 *
 * "Run prefilter" shows the instant lexical candidates; "Run full map" starts a
 * background map job and polls it every few seconds until it succeeds (the finished map
 * is shown here and also lands in Review), fails, or the model declines the input. The
 * description, prefilter, result, error, rejection, and running job all persist per
 * tab (useSessionState), so leaving mid-run and coming back resumes watching the same job.
 *
 * Input the server's junk checks turn away (a 422) or the model declines shows the reasons
 * with a "Map anyway" button that reruns with `force`, skipping both. Editing the text
 * clears a stale rejection; "Clear" (disabled while a map runs) starts over.
 *
 * A remap arrives as `/map?remap=<map id>` (a map's Remap button): the form is prefilled with
 * that map's request and a suggested reason, and a read-only "Supersedes" field names the map
 * the new one will replace — shown only for a remap, and dropped by "Cancel remap" or "Clear".
 * The new map records what it supersedes and why. */
export function MapPage() {
  const [text, setText] = useSessionState(KEY + "text", "");
  const [pre, setPre] = useSessionState<PrefilterResponse | null>(KEY + "prefilter", null);
  const [result, setResult] = useSessionState<Assessment | null>(KEY + "result", null);
  const [error, setError] = useSessionState<string | null>(KEY + "error", null);
  const [rejection, setRejection] = useSessionState<Rejection | null>(KEY + "rejection", null);
  const [activeJob, setActiveJob] = useSessionState<ActiveJob | null>(KEY + "job", null);
  const [supersedes, setSupersedes] = useSessionState<string | null>(KEY + "supersedes", null);
  const [supersedesTitle, setSupersedesTitle] = useSessionState(KEY + "supersedesTitle", "");
  const [remapReason, setRemapReason] = useSessionState(KEY + "remapReason", "");
  const [params, setParams] = useSearchParams();
  const remapParam = params.get("remap");
  // The few hundred ms between clicking "Run full map" and the job id coming back —
  // not persisted, just enough to stop a double click starting two maps.
  const [starting, setStarting] = useState(false);
  const mapping = activeJob !== null || starting;
  const elapsed = useElapsed(activeJob?.startedAt ?? null);

  // Watches the active job until it settles. Keyed on the job id, so it starts both
  // right after "Run full map" and when the page is reopened with a job still running.
  // Unmounting stops the watching, not the job; the next visit picks it back up.
  const jobId = activeJob?.id;
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    /** Records how a finished job ended: the model's rejection, a failure, or the finished map
     * (fetched by its id), then clears the active job. */
    const settle = async (job: MapJob) => {
      if (job.status === "rejected") {
        setRejection({ by: "model", reasons: [job.error ?? "The model found nothing to map."] });
      } else if (job.status === "failed" || !job.map_id) {
        setError(`Mapping failed: ${job.error ?? "no reason given"}`);
      } else {
        const assessment = await getMap(job.map_id);
        if (cancelled) return;
        setResult(assessment);
        // A remap is done once its map exists; the form goes back to an ordinary new map.
        setSupersedes(null);
        setSupersedesTitle("");
        setRemapReason("");
      }
      setActiveJob(null);
    };
    void (async () => {
      try {
        let job = await getMapJob(jobId);
        while (job.status === "pending" || job.status === "running") {
          await new Promise((r) => setTimeout(r, POLL_MS));
          if (cancelled) return;
          job = await getMapJob(jobId);
        }
        if (!cancelled) await settle(job);
      } catch (e) {
        if (cancelled) return;
        setError(errorText(e));
        setActiveJob(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // The setters are stable for the page's lifetime; only a new job id restarts this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Arriving from a map's Remap button: prefill the form from that map, then drop the query
  // parameter so a reload doesn't overwrite edits made since. Never over a map in progress.
  useEffect(() => {
    if (!remapParam) return;
    let cancelled = false;
    void (async () => {
      try {
        if (activeJob) throw new Error("A map is still running — let it finish before remapping.");
        const [original, status] = await Promise.all([getMap(remapParam), getMapStatus(remapParam)]);
        if (cancelled) return;
        if (status.superseded_by) throw new Error(`'${remapParam}' was already remapped as '${status.superseded_by}'.`);
        setText(original.source_text);
        setPre(null);
        setResult(null);
        setError(null);
        setRejection(null);
        setSupersedes(original.id);
        setSupersedesTitle(original.title);
        setRemapReason(suggestedReason(status));
      } catch (e) {
        if (!cancelled) setError(errorText(e));
      } finally {
        if (!cancelled) setParams({}, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // The setters are stable for the page's lifetime; only a new remap request restarts this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remapParam]);

  /** Drops the remap, leaving the form as an ordinary new map. */
  function cancelRemap() {
    setSupersedes(null);
    setSupersedesTitle("");
    setRemapReason("");
  }

  /** Runs the lexical prefilter on the current text — no model, no job. */
  async function runPrefilter() {
    setError(null);
    try {
      setPre(await prefilter(text));
    } catch (e) {
      setError(errorText(e));
    }
  }

  /** Updates the description; any rejection was about the old text, so it goes. */
  function editText(value: string) {
    setText(value);
    setRejection(null); // the reasons were about the old text
  }

  /** Resets the form and every result for a fresh description. */
  function clear() {
    setText("");
    setPre(null);
    setResult(null);
    setError(null);
    setRejection(null);
    cancelRemap();
  }

  /** Starts a map job and records it as the active job, which starts the polling above.
   * `force` is "Map anyway": skip the junk checks and the model's option to decline. A 422
   * becomes a rules rejection (with its reasons) rather than a plain error. */
  async function runFullMap(force = false) {
    setError(null);
    setRejection(null);
    setResult(null);
    setStarting(true);
    try {
      const job = await createMap(text, force, supersedes ? { supersedes, reason: remapReason } : undefined);
      setActiveJob({ id: job.id, startedAt: Date.now() });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) setRejection({ by: "rules", reasons: e.errors });
      else setError(errorText(e));
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <h2 className="font-[family-name:--font-display] text-2xl">{supersedes ? "Remap a change" : "Map a change"}</h2>

      <Card className="flex flex-col gap-3">
        {supersedes && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-[5px]">
              <span className="text-xs text-text/70">Supersedes</span>
              <div className={`${FIELD_STYLES} flex items-center justify-between gap-3 bg-sunken`} aria-readonly="true">
                <Link
                  to={`/review/${encodeURIComponent(supersedes)}`}
                  className="font-mono text-xs text-accent underline"
                >
                  {supersedesTitle || "the earlier map"}
                </Link>
                <button
                  onClick={cancelRemap}
                  disabled={mapping}
                  className="cursor-pointer text-xs text-text/60 underline disabled:cursor-default disabled:opacity-50"
                >
                  Cancel remap
                </button>
              </div>
            </div>
            <TextField
              label="Reason for remapping (recorded on the new map)"
              value={remapReason}
              onChange={setRemapReason}
              placeholder="What changed — e.g. Retool moved Trial → Hold"
            />
          </div>
        )}
        <TextField
          label="Product description"
          multiline
          rows={6}
          value={text}
          onChange={editText}
          placeholder="Employer admins want to pause applications once a department budget is 90% consumed…"
        />
        <div className="flex gap-2">
          <Button onClick={runPrefilter} isDisabled={!text.trim()}>
            Run prefilter
          </Button>
          <Button
            variant="primary"
            onClick={() => runFullMap()}
            isDisabled={!text.trim() || mapping || (supersedes !== null && !remapReason.trim())}
          >
            {mapping ? (
              <span className="flex items-center gap-2">
                <Spinner className="h-3.5 w-3.5" />
                Mapping…
              </span>
            ) : (
              "Run full map"
            )}
          </Button>
          <Button
            onClick={clear}
            isDisabled={mapping || (!text && !pre && !result && !error && !rejection && !supersedes)}
            className="ml-auto"
          >
            Clear
          </Button>
        </div>
        {error && <p className="text-sm text-warn">{error}</p>}
      </Card>

      {rejection && !mapping && <RejectionView rejection={rejection} onMapAnyway={() => runFullMap(true)} />}

      {mapping && <MappingProgress elapsed={elapsed} />}

      {pre && !result && !mapping && <PrefilterView pre={pre} />}
      {result && <ResultView assessment={result} />}
    </div>
  );
}

/** Seconds since `startedAt`, ticking once a second; 0 when there's nothing running.
 * From a stored start time, so the clock is right after returning to the page. */
function useElapsed(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return startedAt === null ? 0 : Math.max(0, Math.round((now - startedAt) / 1000));
}

/** Why the input was turned away — by the server's rules ("This doesn't look like a
 * change request") or by the model ("found nothing to map") — with a "Map anyway"
 * override that warns a full map takes a minute or more. */
function RejectionView({ rejection, onMapAnyway }: { rejection: Rejection; onMapAnyway: () => void }) {
  return (
    <Card role="alert" className="flex flex-col gap-3 border-warn-line bg-warn-bg">
      <div>
        <h3 className="font-medium text-warn">
          {rejection.by === "rules" ? "This doesn't look like a change request" : "The model found nothing to map"}
        </h3>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {rejection.reasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onMapAnyway}>Map anyway</Button>
        <span className="text-sm text-text/60">
          Skips these checks{rejection.by === "rules" ? " and the model's" : ""} — a full map takes a minute or more.
        </span>
      </div>
    </Card>
  );
}

/** The running-map status: a spinner, an elapsed m:ss clock, and the reassurance that it's
 * safe to leave — the finished map appears under Review. */
function MappingProgress({ elapsed }: { elapsed: number }) {
  const minutes = Math.floor(elapsed / 60);
  const clock = `${minutes}:${String(elapsed % 60).padStart(2, "0")}`;
  return (
    <Card role="status" aria-live="polite" className="flex items-start gap-4">
      <Spinner className="mt-0.5 h-6 w-6 text-g-orange" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-medium">
          Mapping your change… {/* Ticks every second — hidden from the live region so it isn't re-announced. */}
          <span aria-hidden="true" className="font-mono text-sm text-text/60 tabular-nums">
            {clock}
          </span>
        </p>
        <p className="text-sm text-text/60">
          The model is matching your change against the capability registry, the standards, and the tools and practices
          catalog, then suggesting what could help — this usually takes a minute or more. You can leave this page; the
          finished map appears under <strong>Review</strong>.
        </p>
      </div>
    </Card>
  );
}

/** The prefilter's lexical candidates, each with its signals (alias signals highlighted)
 * and any scope-exclude veto, plus out-of-scope signals. Says up front that it finds
 * vocabulary, not meaning — no hits is normal for business-language input. */
function PrefilterView({ pre }: { pre: PrefilterResponse }) {
  return (
    <Card>
      <h3 className="mb-2 font-medium">
        Lexical candidates ({pre.candidates.length})
        <span className="ml-2 text-xs font-normal text-text/50">
          Recall-limited by design — it finds vocabulary, not meaning. The full map always re-reads every capability.
        </span>
      </h3>
      {pre.candidates.length === 0 ? (
        <p className="text-sm text-text/50">No lexical hits — normal for business-language input.</p>
      ) : (
        <ul className="flex flex-col gap-2 text-sm">
          {pre.candidates.map((c) => (
            <li key={c.capability}>
              <CapabilityLink id={c.capability} />{" "}
              {c.signals.map((s, i) => (
                <Badge key={i} tone={s.tier === "alias" ? "accent" : "neutral"} className="ml-1">
                  {s.tier}/{s.how}
                </Badge>
              ))}
              {c.vetoes.length > 0 && (
                <Badge tone="warn" className="ml-1">
                  veto: {c.vetoes[0].phrase}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
      {Object.keys(pre.out_of_scope_signals).length > 0 && (
        <div className="mt-3">
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Out-of-scope signals</h4>
          <ul className="text-sm text-text/70">
            {Object.entries(pre.out_of_scope_signals).map(([id]) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** The finished map as the model wrote it: verdict, when it read the catalog, the
 * capabilities touched (with confidence and the requirement each was matched on),
 * contested pairs and the missing rule between them, and unmatched requirements. Points
 * to Review, since the map itself is never edited. */
function ResultView({ assessment: a }: { assessment: Assessment }) {
  return (
    <Card className="flex flex-col gap-4">
      <div>
        <h3 className="font-[family-name:--font-display] text-xl">{a.title}</h3>
        <p className="text-sm text-text/60">
          Verdict <em>{a.verdict}</em> · catalog as of {ago(a.catalog_read_at ?? a.created_at)}
        </p>
      </div>

      {a.supersedes && (
        <p className="text-sm text-text/70">
          Remap of{" "}
          <Link to={`/review/${encodeURIComponent(a.supersedes)}`} className="text-accent underline">
            {a.supersedes_title ?? "an earlier map"}
          </Link>
          {a.remap_reason && <> — {a.remap_reason}</>}
        </p>
      )}

      {a.matches.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Capabilities touched</h4>
          <ul className="flex flex-col gap-1.5 text-sm">
            {a.matches.map((m) => (
              <li key={m.capability}>
                <CapabilityLink id={m.capability} /> <Badge>{m.confidence}</Badge> — "{m.requirement}"
              </li>
            ))}
          </ul>
        </section>
      )}

      <SuggestionsView suggestions={a.suggestions} />

      {a.contested.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Contested</h4>
          <ul className="flex flex-col gap-2 text-sm">
            {a.contested.map((c, i) => (
              <li key={i}>
                <strong>
                  <CapabilityLinks ids={c.between} separator=" ↔ " />
                </strong>{" "}
                — "{c.requirement}"<p className="text-text/60">{c.missing_rule}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {a.unmatched.length > 0 && (
        <section>
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-text/60 uppercase">Unmatched</h4>
          <ul className="flex flex-col gap-1.5 text-sm">
            {a.unmatched.map((u, i) => (
              <li key={i}>
                "{u.requirement}" — suspected {u.suspected}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-sm text-text/60">
        Written once.{" "}
        <Link to={`/review/${encodeURIComponent(a.id)}`} className="text-accent underline">
          Review this map
        </Link>{" "}
        to sign off or correct it.
      </p>
    </Card>
  );
}
